import React from 'react';
import clsx from 'clsx';

import FileAttachmentCard from '../FileAttachmentCard/FileAttachmentCard';
import useLibrary, { fileKey } from './useLibrary';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';
import { ASK_AICA_LIBRARY_LABEL } from '../../../../../../../../store/dashboard/askAica/constants';

import styles from './Library.module.scss';

// Caps the initial-load stagger so a long file list doesn't crawl in one-by-one.
const MAX_STAGGER_INDEX = 8;
const STAGGER_STEP_MS = 60;

interface LibraryProps {
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
    /** URL of the file currently being opened — shows a spinner on that card. */
    openingFileUrl?: string | null;
    /** Mobile full-page mode: fills its parent and drops the header (no Data Room on mobile). */
    fullPage?: boolean;
}

const Library: React.FC<LibraryProps> = ({ onOpenFile, onAddFileToReports, openingFileUrl, fullPage = false }) => {
    const { files, isLoading, isLenderOrPartner, handleDownloadFile, handleViewInDataroom, handleOpenFile, handleAddFileToReports } =
        useLibrary({ onOpenFile, onAddFileToReports });

    const renderLoadingShimmer = () => (
        <div className={styles.LoadingShimmer}>
            {[1, 2].map((index) => (
                <div key={index} className={styles.ShimmerItem}>
                    <div className={styles.ShimmerIcon} />
                    <div className={styles.ShimmerContent}>
                        <div className={`${styles.ShimmerLine} ${styles.medium}`} />
                        <div className={`${styles.ShimmerLine} ${styles.short}`} />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className={clsx(styles.Library, fullPage && styles.FullPage)}>
            {!fullPage && (
                <div className={styles.Header}>
                    <span className={styles.Title}>{ASK_AICA_LIBRARY_LABEL}</span>
                    {!isLenderOrPartner && (
                        <button type="button" className={styles.ViewInDataroomLink} onClick={handleViewInDataroom}>
                            View in Dataroom
                        </button>
                    )}
                </div>
            )}

            <div className={styles.FileList}>
                {isLoading && files.length === 0 ? (
                    // Only shimmer for the true initial load — a background refetch (e.g. after a
                    // new file finishes generating) keeps showing the existing list so the newly
                    // added card can animate in, instead of flashing to a shimmer and back.
                    renderLoadingShimmer()
                ) : files.length === 0 ? (
                    <p className={styles.EmptyState}>No files generated yet</p>
                ) : (
                    files.map((file, index) => (
                        <div
                            key={fileKey(file)}
                            className={styles.FileEnterWrapper}
                            style={{ animationDelay: `${Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP_MS}ms` }}
                        >
                            <FileAttachmentCard
                                fileUrl={file.fileUrl}
                                fileName={file.fileName}
                                fileType={file.fileType}
                                createdAt={file.createdAt}
                                onDownload={handleDownloadFile}
                                onCardClick={() => handleOpenFile(file)}
                                onAddToReports={onAddFileToReports ? () => handleAddFileToReports(file) : undefined}
                                loading={!!openingFileUrl && openingFileUrl === file.fileUrl}
                                truncateName={false}
                                className={styles.LibraryFileCard}
                            />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Library;
